import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import type { PipelineOption } from '@/types/ai-pipeline';

interface PipelineOptionCardProps {
  option: PipelineOption;
  onSelect: () => void;
  disabled?: boolean;
}

export function PipelineOptionCard({ option, onSelect, disabled = false }: PipelineOptionCardProps) {
  const confidenceColor = option.confidence >= 0.8
    ? 'text-green-600'
    : option.confidence >= 0.6
    ? 'text-yellow-600'
    : 'text-red-600';

  return (
    <Card
      className={`transition-colors ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-primary'}`}
      onClick={disabled ? undefined : onSelect}
    >
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{option.name}</CardTitle>
          <Badge variant="outline" className={`text-xs ${confidenceColor}`}>
            {Math.round(option.confidence * 100)}% confidence
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="py-2 px-4 space-y-2">
        <p className="text-xs text-muted-foreground">{option.description}</p>

        <div className="flex items-center gap-1 flex-wrap">
          {option.nodes.map((node, i) => (
            <span key={i} className="flex items-center gap-1">
              <Badge variant="secondary" className="text-xs">
                {node.type}
              </Badge>
              {i < option.nodes.length - 1 && (
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
              )}
            </span>
          ))}
        </div>

        <Button
          size="sm"
          variant="outline"
          className="w-full mt-2"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            if (!disabled) onSelect();
          }}
        >
          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
          Use this pipeline
        </Button>
      </CardContent>
    </Card>
  );
}
